import React, { forwardRef, useId } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  RegisterOptions,
} from 'react-hook-form';

import type { SelectOptionDataType } from '@/components/shared/ui/forms/selects/types/SelectOptionDataType';

import Description from '../description/Description';
import { SelectProps } from '../selects/types/SelectProps';

interface SelectHookProps extends Omit<SelectProps, 'variant'> {
  name: string;
  control: Control<any>;
  options: SelectOptionDataType[];
  rules?: RegisterOptions;
  errors?: FieldErrors<any>;
  instanceId?: string;
  description?: string;
  shouldUnregister?: boolean;
}

export default function withSelectHookForm(
  Component: React.ForwardRefExoticComponent<
    Omit<SelectHookProps, 'ref' | 'control'> & React.RefAttributes<HTMLElement>
  >,
) {
  const WrappedComponent = forwardRef(
    (
      {
        name,
        control,
        options,
        rules,
        errors,
        description,
        shouldUnregister,
        styleWidthSize,
        ...props
      }: SelectHookProps & {
        control?: any;
        register?: any;
        errors?: any;
        rules?: any;
        watch?: any;
      },
      ref: React.ForwardedRef<HTMLElement>,
    ) => {
      const id = useId();
      const descriptionId = useId();
      const hasError = !!errors?.[name];
      const hasDescription = hasError || description;
      const {
        instanceId,
        inputId,
        'aria-describedby': ariaDescribedBy,
        'aria-invalid': ariaInvalid,
        ...selectProps
      } = props as any;
      const describedBy = [ariaDescribedBy, hasDescription && descriptionId]
        .filter(Boolean)
        .join(' ');

      return (
        <div>
          <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            render={({ field }) => {
              return (
                <Component
                  instanceId={instanceId ?? id + field.name}
                  inputId={inputId ?? `${id}-${field.name}-input`}
                  options={options}
                  hasError={hasError}
                  styleWidthSize={styleWidthSize}
                  aria-invalid={ariaInvalid ?? (hasError || undefined)}
                  aria-describedby={describedBy || undefined}
                  {...selectProps}
                  {...field}
                  ref={ref}
                />
              );
            }}
          />
          <Description
            id={descriptionId}
            name={name}
            errors={errors}
            description={description}
            styleWidthSize={styleWidthSize}
          />
        </div>
      );
    },
  );
  return WrappedComponent;
}
